import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { LogOut, Users, BookOpen, Bot, TrendingUp, Trash2, Plus, Sparkles, Euro, Activity, KeyRound, LogIn } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useAuth, roleHome } from "../../context/AuthContext";
import { Logo } from "../../components/common";
import { EXERCISE_TYPES, MATH_EXERCISE_TYPES, DIFFICULTIES, SUBJECTS } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";

export default function AdminPanel() {
  const navigate = useNavigate();
  const { logout, impersonate } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "begrijpend_lezen", template: "", active: true });
  const [exFilter, setExFilter] = useState("alle");
  const [whitelist, setWhitelist] = useState([]);
  const [newEmail, setNewEmail] = useState("");

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/users").then(({ data }) => setUsers(data.users));
    api.get("/admin/exercises").then(({ data }) => setExercises(data.exercises));
    api.get("/admin/prompts").then(({ data }) => setPrompts(data.prompts));
    api.get("/admin/whitelist").then(({ data }) => setWhitelist(data.emails));
  };
  useEffect(() => { loadAll(); }, []);

  const delExercise = async (id) => { await api.delete(`/admin/exercises/${id}`); toast.success("Verwijderd"); loadAll(); };
  const delPrompt = async (id) => { await api.delete(`/admin/prompts/${id}`); toast.success("Prompt verwijderd"); loadAll(); };
  const addWhitelist = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    try {
      const { data } = await api.post("/admin/whitelist", { email });
      setWhitelist(data.emails);
      setNewEmail("");
      toast.success("Gratis toegang toegevoegd");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const delWhitelist = async (email) => {
    try {
      const { data } = await api.delete(`/admin/whitelist/${encodeURIComponent(email)}`);
      setWhitelist(data.emails);
      toast.success("Gratis toegang verwijderd");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const savePrompt = async () => {
    try { await api.post("/admin/prompts", form); toast.success("Prompt opgeslagen"); setOpen(false); setForm({ name: "", type: "begrijpend_lezen", template: "", active: true }); loadAll(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const roleBadge = (r) => ({ child: "bg-blue-100 text-blue-700", parent: "bg-green-100 text-green-700", admin: "bg-purple-100 text-purple-700" }[r] || "bg-slate-100 text-slate-600");

  const loginAs = async (u) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${u.id}`);
      impersonate(data.access_token, data.user);
      toast.success(`Je bekijkt nu als ${data.user.name}`);
      navigate(roleHome(data.user.role));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <header className="bg-white sticky top-0 z-30 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo /><span className="px-2.5 py-1 rounded-lg bg-purple-100 text-purple-700 font-bold text-xs">ADMIN</span></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/ouder")} className="rounded-full font-bold gap-1.5" data-testid="admin-to-parent-btn"><Users className="w-4 h-4" /> Ouderdashboard</Button>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="admin-logout-btn"><LogOut className="w-5 h-5" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 pt-8">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-6">Beheerpaneel</h1>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Gebruikers", value: stats.total_users, sub: `${stats.total_children} kind · ${stats.total_parents} ouder`, icon: Users, color: "text-blue-600 bg-blue-50" },
              { label: "Oefeningen", value: stats.total_exercises, sub: `${stats.ai_exercises} via AI`, icon: BookOpen, color: "text-green-600 bg-green-50" },
              { label: "Pogingen", value: stats.total_attempts, sub: "totaal gemaakt", icon: Activity, color: "text-orange-600 bg-orange-50" },
              { label: "Omzet", value: `€${stats.revenue}`, sub: "betaalde abonnementen", icon: Euro, color: "text-purple-600 bg-purple-50" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`admin-stat-${s.label}`}>
                <div className={`w-11 h-11 rounded-xl ${s.color} flex items-center justify-center mb-3`}><s.icon className="w-6 h-6" /></div>
                <p className="font-heading text-2xl font-black text-slate-900">{s.value}</p>
                <p className="text-sm text-slate-500 font-bold">{s.label}</p>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="users">
          <TabsList className="bg-white border border-slate-200 rounded-2xl p-1 h-auto flex-wrap">
            <TabsTrigger value="users" className="rounded-xl font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-users">Gebruikers</TabsTrigger>
            <TabsTrigger value="exercises" className="rounded-xl font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-exercises">Oefeningen</TabsTrigger>
            <TabsTrigger value="prompts" className="rounded-xl font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-prompts">AI Prompts</TabsTrigger>
            <TabsTrigger value="whitelist" className="rounded-xl font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-whitelist">Gratis toegang</TabsTrigger>
            <TabsTrigger value="stats" className="rounded-xl font-bold data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-stats">Statistieken</TabsTrigger>
          </TabsList>

          {/* Users */}
          <TabsContent value="users" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Naam</TableHead><TableHead>E-mail</TableHead><TableHead>Rol</TableHead><TableHead>XP</TableHead><TableHead>Toegang</TableHead><TableHead className="text-right">Actie</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-bold text-slate-800">{u.name}</TableCell>
                      <TableCell className="text-slate-500">{u.email}</TableCell>
                      <TableCell><span className={`px-2.5 py-1 rounded-lg font-bold text-xs ${roleBadge(u.role)}`}>{u.role}</span></TableCell>
                      <TableCell className="font-semibold">{u.xp ?? "—"}</TableCell>
                      <TableCell>{u.whitelisted ? <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700">Gratis</span> : <span className="text-slate-400 text-xs">—</span>}</TableCell>
                      <TableCell className="text-right">
                        {u.role === "admin" ? <span className="text-slate-300 text-xs">—</span> : (
                          <Button variant="outline" size="sm" onClick={() => loginAs(u)} className="rounded-full font-bold gap-1.5" data-testid={`impersonate-${u.id}`}>
                            <LogIn className="w-3.5 h-3.5" /> Inloggen als
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Exercises */}
          <TabsContent value="exercises" className="mt-5">
            <div className="flex gap-2 mb-4">
              {[["alle", "Alle vakken"], ["lezen", "📖 Lezen"], ["rekenen", "🔢 Rekenen"]].map(([key, label]) => (
                <button key={key} onClick={() => setExFilter(key)} data-testid={`ex-filter-${key}`}
                  className={`px-4 py-2 rounded-full font-bold text-sm transition-all ${exFilter === key ? "bg-primary text-white shadow" : "bg-white text-slate-500 border border-slate-200"}`}>{label}</button>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Titel</TableHead><TableHead>Vak</TableHead><TableHead>Type</TableHead><TableHead>Niveau</TableHead><TableHead>Bron</TableHead><TableHead>Vragen</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {exercises.filter((e) => exFilter === "alle" || (e.subject || "lezen") === exFilter).map((e) => (
                    <TableRow key={e.id} data-testid={`exercise-row-${e.id}`}>
                      <TableCell className="font-bold text-slate-800">{e.title}</TableCell>
                      <TableCell><span className={`px-2 py-0.5 rounded-md text-xs font-bold ${(e.subject || "lezen") === "rekenen" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{SUBJECTS[e.subject || "lezen"]?.label}</span></TableCell>
                      <TableCell className="text-slate-500">{EXERCISE_TYPES[e.type]?.label || MATH_EXERCISE_TYPES[e.type]?.label || e.type}</TableCell>
                      <TableCell>{DIFFICULTIES[e.difficulty] || e.difficulty}</TableCell>
                      <TableCell><span className={`px-2 py-0.5 rounded-md text-xs font-bold ${e.source === "ai" ? "bg-violet-100 text-violet-700" : e.source === "generated" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{e.source === "ai" ? "AI" : e.source === "generated" ? "Auto" : "Seed"}</span></TableCell>
                      <TableCell>{e.num_questions}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => delExercise(e.id)} className="text-red-400 hover:text-red-600" data-testid={`del-ex-${e.id}`}><Trash2 className="w-4 h-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Prompts */}
          <TabsContent value="prompts" className="mt-5">
            <div className="flex justify-end mb-4">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button className="rounded-full font-bold bg-accent hover:bg-orange-600" data-testid="add-prompt-btn"><Plus className="w-4 h-4 mr-1.5" /> Nieuwe prompt</Button></DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader><DialogTitle className="font-heading">Nieuwe AI-prompt</DialogTitle>
                    <DialogDescription>Maak een herbruikbaar promptsjabloon voor AI-oefeningen.</DialogDescription>
                  </DialogHeader>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Naam" className="rounded-xl border-2 h-11" data-testid="prompt-name-input" />
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-xl border-2 h-11 px-3 font-medium" data-testid="prompt-type-select">
                    {Object.entries(EXERCISE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <Textarea value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} placeholder="Prompt sjabloon..." rows={5} className="rounded-xl border-2" data-testid="prompt-template-input" />
                  <DialogFooter><Button onClick={savePrompt} className="rounded-full font-bold bg-primary hover:bg-blue-600" data-testid="save-prompt-btn">Opslaan</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {prompts.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`prompt-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2"><Bot className="w-5 h-5 text-primary" /><h3 className="font-heading font-bold text-slate-900">{p.name}</h3></div>
                    <Button variant="ghost" size="icon" onClick={() => delPrompt(p.id)} className="text-red-400 hover:text-red-600" data-testid={`del-prompt-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-xs font-bold">{EXERCISE_TYPES[p.type]?.label || p.type}</span>
                  <p className="mt-3 text-sm text-slate-500 font-medium">{p.template}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Whitelist / gratis toegang */}
          <TabsContent value="whitelist" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-5 h-5 text-emerald-600" />
                <h3 className="font-heading font-bold text-slate-900">Gratis toegang</h3>
              </div>
              <p className="text-sm text-slate-500 font-medium mb-4">E-mailadressen op deze lijst krijgen permanente gratis volledige toegang (geen abonnement nodig).</p>
              <div className="flex gap-2 mb-5">
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") addWhitelist(); }}
                  placeholder="naam@voorbeeld.nl" type="email"
                  className="rounded-xl border-2 h-11" data-testid="whitelist-email-input" />
                <Button onClick={addWhitelist} className="rounded-full font-bold bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="add-whitelist-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Toevoegen
                </Button>
              </div>
              {whitelist.length === 0 ? (
                <p className="text-sm text-slate-400 font-medium py-4 text-center" data-testid="whitelist-empty">Nog geen adressen met gratis toegang.</p>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  {whitelist.map((w) => (
                    <div key={w.email} className="flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`whitelist-row-${w.email}`}>
                      <span className="font-semibold text-slate-700 text-sm">{w.email}</span>
                      <Button variant="ghost" size="icon" onClick={() => delWhitelist(w.email)} className="text-red-400 hover:text-red-600" data-testid={`del-whitelist-${w.email}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Stats */}
          <TabsContent value="stats" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> Oefeningen per type</p>
              {stats && (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={stats.exercises_by_type.map((t) => ({ name: EXERCISE_TYPES[t.type]?.label || t.type, count: t.count }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#94A3B8" }} />
                    <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                    <Bar dataKey="count" fill="#F97316" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
